import React from 'react'
import { CacheProvider } from '@emotion/react'
import { Provider } from 'react-redux'
import createCache from '@emotion/cache'
import { spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { store } from '@core/Store'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import { openShareOutgoing } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/emitter'
import { getKambiPendingBetCoupon } from '@core/Areas/KambiSports/Player/helpers/api'
import { useBetLoopSportTooltips } from '@core/Areas/BetLoop/hooks/useBetLoopSportTooltips'
import { useOnboardingTooltips } from '@core/Areas/BetLoop/hooks/useOnboardingTooltips'
import { BetLoopSportTooltip } from '@core/Areas/BetLoop/hooks/useBetLoopSportTooltips/Components/BetLoopSportTooltip'
import { selectActiveKambiJurisdiction } from '../../Store/selectors'

const enum LocalConstants {
  KambiReceiptSeenCountStorageKey = 'tooltip:bet-loop-sport-kambi-receipt',
}

export const ShareBetButton: React.FC<{ couponRef: string }> = ({ couponRef }) => {
  const appState = store.getState()
  const features = getFeatures(appState)
  const kambiJurisdiction = selectActiveKambiJurisdiction(appState)
  const isBetLoopSportsFeatureActive = features['bet-loop-sports']

  if (kambiJurisdiction === 'rwwasa' || !isBetLoopSportsFeatureActive) return null

  const cleanCouponRefValue = Number.parseInt(couponRef)
  if (!cleanCouponRefValue || typeof cleanCouponRefValue !== 'number') return null

  return (
    <Provider store={store}>
      <ShareBetButtonComponent couponRef={cleanCouponRefValue} />
    </Provider>
  )
}

// ===============
// Local Component
// ===============

const ShareBetButtonComponent: React.FC<{ couponRef: number }> = ({ couponRef }) => {
  const shareButtonRef = React.useRef<HTMLButtonElement>(null)
  const rootContainerRef = React.useRef<HTMLDivElement>(null)

  const [refCurr, setRefCurr] = React.useState<HTMLDivElement | null>(null)
  const [shareCoupon, setShareCoupon] = React.useState<KambiHistoryCoupon | null>(null)
  const [isFetchingCouponData, setIsFetchingCouponData] = React.useState(false)

  // Force re-render to apply styles
  React.useEffect(() => {
    setRefCurr(rootContainerRef.current)
  }, [])

  const { hasShownAllOnboardingTips } = useOnboardingTooltips('bet-loop-share-bet-button')

  const { tooltipRef, onTooltipClick, handleDismissTooltip, isSportTooltipVisible } =
    useBetLoopSportTooltips(
      LocalConstants.KambiReceiptSeenCountStorageKey,
      hasShownAllOnboardingTips,
      shareButtonRef,
      0
    )

  const handleShareClick = () => {
    onTooltipClick()

    if (shareCoupon) {
      openShareOutgoing({ contextType: 'kambi', share: { coupon: shareCoupon } })
      return
    }

    setIsFetchingCouponData(true)
    getKambiPendingBetCoupon(couponRef)
      .then(response => {
        if (!response) throw Error('Kambi: No coupon found')
        const coupon = response[0]
        openShareOutgoing({ contextType: 'kambi', share: { coupon } })
        setShareCoupon(coupon)
      })
      .catch(() => {
        const title = "Sorry, we're unable to share at this time"
        addToast({ id: 'kambi-share-error', title, icon: 'warning', theme: 'dark' })
      })
      .finally(() => setIsFetchingCouponData(false))
  }

  return (
    <div ref={rootContainerRef} style={{ minHeight: '4rem' }}>
      <CacheProvider
        value={createCache({
          prepend: true,
          key: 'kambi-bet-slip-share-button',
          container: refCurr as HTMLDivElement,
        })}
      >
        {refCurr && (
          <div style={{ padding: `${spacing.smx1} 1rem 0` }}>
            <Button
              isFullWidth
              ref={shareButtonRef}
              color='secondary_grey'
              onClick={handleShareClick}
              data-testid='ShareBetButton'
              isLoading={isFetchingCouponData}
            >
              <Icon name='LineShare02' size='2rem' /> Share Bet
            </Button>

            {isSportTooltipVisible && (
              <BetLoopSportTooltip
                triggerElRef={tooltipRef}
                onDismissed={handleDismissTooltip}
                tooltipId='bet-loop-sport-kambi-receipt'
                yPos={10}
                xPos={-3}
              />
            )}
          </div>
        )}
      </CacheProvider>
    </div>
  )
}
